import React, { useEffect, useState, useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { Zap, Box, ArrowRight, Sparkles, Cpu, ArrowRightCircle, ArrowLeftCircle, Type } from 'lucide-react';

// Memoized node component - mobile optimized
const PipelineNode = memo(({ icon: Icon, label, color, delay, position }) => {
    const colorClasses = {
        cyan: 'border-neon-blue/60 bg-neon-blue/5',
        purple: 'border-purple-500/60 bg-purple-500/5',
        green: 'border-green-500/60 bg-green-500/5',
        orange: 'border-orange-500/60 bg-orange-500/5'
    };

    const iconColors = {
        cyan: 'text-neon-blue',
        purple: 'text-purple-400',
        green: 'text-green-400',
        orange: 'text-orange-400'
    };

    return (
        <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay, duration: 0.4, ease: 'easeOut' }}
            className={`${colorClasses[color]} border-2 rounded-xl p-3 sm:p-4 will-change-transform ${position}`}
        >
            <div className="flex flex-col items-center gap-1.5 sm:gap-2">
                <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColors[color]}`} />
                <span className="text-[10px] sm:text-xs font-mono text-slate-400 tracking-wider text-center">{label}</span>
            </div>
        </motion.div>
    );
});

PipelineNode.displayName = 'PipelineNode';

// Memoized stat card
const StatCard = memo(({ value, label, delay }) => (
    <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay, duration: 0.5, ease: 'easeOut' }}
        className="bg-slate-900/80 border border-white/10 rounded-xl p-3 sm:p-4 text-center will-change-transform"
    >
        <div className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-0.5 sm:mb-1">{value}</div>
        <div className="text-[10px] sm:text-xs text-slate-500 font-mono uppercase tracking-wider">{label}</div>
    </motion.div>
));

StatCard.displayName = 'StatCard';

const ScrollPreview = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        // Check for reduced motion preference
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);

        // Intersection Observer for lazy loading animations
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1, rootMargin: '50px' }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Reduce animations if user prefers reduced motion
    const animationProps = prefersReducedMotion
        ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
        : { initial: { y: 30, opacity: 0 }, animate: isVisible ? { y: 0, opacity: 1 } : {} };

    return (
        <section
            ref={sectionRef}
            className="relative py-16 sm:py-20 md:py-28 bg-cyber-black overflow-hidden"
        >
            {/* Lightweight background - CSS only */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,243,255,0.06)_0%,transparent_60%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:40px_40px] sm:bg-[size:50px_50px]" />

                {/* Simple gradient orbs */}
                <div
                    className="absolute top-16 left-[5%] w-32 sm:w-48 h-32 sm:h-48 rounded-full opacity-30"
                    style={{ background: 'radial-gradient(circle, rgba(0,243,255,0.15) 0%, transparent 70%)' }}
                />
                <div
                    className="absolute bottom-16 right-[8%] w-40 sm:w-56 h-40 sm:h-56 rounded-full opacity-25"
                    style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)' }}
                />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section header */}
                <motion.div
                    {...animationProps}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-10 sm:mb-14 md:mb-18"
                >
                    <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/5 border border-white/10 mb-4 sm:mb-5">
                        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-neon-blue" />
                        <span className="text-xs sm:text-sm font-mono text-slate-400">INTELLIGENT AUTOMATION</span>
                    </div>

                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black text-white tracking-tight mb-3 sm:mb-4">
                        Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-blue via-purple-400 to-green-400">Powerful</span> Pipelines
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg text-slate-500 max-w-2xl mx-auto px-4">
                        Connect, transform, and deploy with our visual pipeline builder
                    </p>
                </motion.div>

                {/* Pipeline visualization - MOBILE: stacked grid, DESKTOP: absolute positioning */}
                {isVisible && (
                    <>
                        {/* Mobile Layout: 2x2 Grid */}
                        <div className="grid grid-cols-2 gap-4 sm:hidden mb-10">
                            <PipelineNode
                                icon={ArrowRightCircle}
                                label="INPUT"
                                color="cyan"
                                delay={0.2}
                                position=""
                            />
                            <PipelineNode
                                icon={Cpu}
                                label="LLM ENGINE"
                                color="purple"
                                delay={0.3}
                                position=""
                            />
                            <PipelineNode
                                icon={Type}
                                label="TEXT"
                                color="orange"
                                delay={0.4}
                                position=""
                            />
                            <PipelineNode
                                icon={ArrowLeftCircle}
                                label="OUTPUT"
                                color="green"
                                delay={0.5}
                                position=""
                            />
                        </div>

                        {/* Desktop Layout: Absolute positioning with SVG lines */}
                        <div className="hidden sm:block relative h-[280px] md:h-[320px] lg:h-[380px] mb-14">
                            {/* Connection SVG */}
                            <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                                <defs>
                                    <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#00f3ff" stopOpacity="0.5" />
                                        <stop offset="50%" stopColor="#a855f7" stopOpacity="0.5" />
                                        <stop offset="100%" stopColor="#22c55e" stopOpacity="0.5" />
                                    </linearGradient>
                                </defs>

                                {/* Connection lines */}
                                <line x1="18%" y1="35%" x2="37%" y2="50%" stroke="url(#lineGrad)" strokeWidth="2" strokeDasharray="6 4" opacity="0.6" />
                                <line x1="43%" y1="50%" x2="57%" y2="30%" stroke="url(#lineGrad)" strokeWidth="2" strokeDasharray="6 4" opacity="0.6" />
                                <line x1="63%" y1="30%" x2="82%" y2="45%" stroke="url(#lineGrad)" strokeWidth="2" strokeDasharray="6 4" opacity="0.6" />
                            </svg>

                            {/* Absolute positioned nodes for desktop */}
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2, duration: 0.4 }}
                                className="absolute border-neon-blue/60 bg-neon-blue/5 border-2 rounded-xl p-4 will-change-transform"
                                style={{ left: '15%', top: '35%', transform: 'translate(-50%, -50%)' }}
                            >
                                <div className="flex flex-col items-center gap-2">
                                    <ArrowRightCircle className="w-6 h-6 text-neon-blue" />
                                    <span className="text-xs font-mono text-slate-400 tracking-wider">INPUT</span>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.3, duration: 0.4 }}
                                className="absolute border-purple-500/60 bg-purple-500/5 border-2 rounded-xl p-4 will-change-transform"
                                style={{ left: '40%', top: '50%', transform: 'translate(-50%, -50%)' }}
                            >
                                <div className="flex flex-col items-center gap-2">
                                    <Cpu className="w-6 h-6 text-purple-400" />
                                    <span className="text-xs font-mono text-slate-400 tracking-wider">LLM ENGINE</span>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.4, duration: 0.4 }}
                                className="absolute border-orange-500/60 bg-orange-500/5 border-2 rounded-xl p-4 will-change-transform"
                                style={{ left: '60%', top: '30%', transform: 'translate(-50%, -50%)' }}
                            >
                                <div className="flex flex-col items-center gap-2">
                                    <Type className="w-6 h-6 text-orange-400" />
                                    <span className="text-xs font-mono text-slate-400 tracking-wider">TEXT</span>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.5, duration: 0.4 }}
                                className="absolute border-green-500/60 bg-green-500/5 border-2 rounded-xl p-4 will-change-transform"
                                style={{ left: '85%', top: '45%', transform: 'translate(-50%, -50%)' }}
                            >
                                <div className="flex flex-col items-center gap-2">
                                    <ArrowLeftCircle className="w-6 h-6 text-green-400" />
                                    <span className="text-xs font-mono text-slate-400 tracking-wider">OUTPUT</span>
                                </div>
                            </motion.div>

                            {/* Pulse ring - CSS only */}
                            {!prefersReducedMotion && (
                                <div
                                    className="absolute border-2 border-purple-500/30 rounded-full animate-ping"
                                    style={{
                                        left: '40%',
                                        top: '50%',
                                        width: '80px',
                                        height: '80px',
                                        transform: 'translate(-50%, -50%)',
                                        animationDuration: '2s'
                                    }}
                                />
                            )}
                        </div>
                    </>
                )}

                {/* Stats row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-5 mb-8 sm:mb-10">
                    <StatCard value="500+" label="Integrations" delay={0.6} />
                    <StatCard value="10x" label="Faster Setup" delay={0.7} />
                    <StatCard value="99.9%" label="Uptime" delay={0.8} />
                    <StatCard value="<50ms" label="Latency" delay={0.9} />
                </div>

                {/* CTA buttons */}
                <motion.div
                    {...animationProps}
                    transition={{ delay: 1, duration: 0.5 }}
                    className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
                >
                    <button className="group w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 bg-gradient-to-r from-neon-blue to-cyan-400 text-black font-bold rounded-lg shadow-lg shadow-neon-blue/20 hover:shadow-neon-blue/30 transition-shadow active:scale-[0.98]">
                        <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm sm:text-base">Start Building</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>

                    <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 bg-transparent border border-white/10 text-white font-semibold rounded-lg hover:bg-white/5 hover:border-white/20 transition-colors active:scale-[0.98]">
                        <Box className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm sm:text-base">View Templates</span>
                    </button>
                </motion.div>
            </div>

            {/* Bottom decorative line */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-blue/40 to-transparent" />
        </section>
    );
};

export default memo(ScrollPreview);
